import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { ClientSiblingPayload } from '../../../../../../common';

export default function useIntakeClientSiblings() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const siblings = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.family?.client_siblings ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createClientSiblingLoading ||
      state.intakeQuestionnaire.updateClientSiblingLoading ||
      state.intakeQuestionnaire.deleteClientSiblingLoading,
  );

  const create = useCallback(
    async (data: ClientSiblingPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeClientSibling({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeClientSibling.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (siblingId: number, data: ClientSiblingPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeClientSibling({
          intakeQuestionnaireId,
          siblingId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeClientSibling.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (siblingId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeClientSibling({
          intakeQuestionnaireId,
          siblingId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeClientSibling.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return { siblings, isSaving, create, update, remove };
}
